import {
  ITemplateTreeNode,
  ISceneGraph,
  PropertyType,
  PropValue,
  IResource,
} from 'models';
import { traverseTree, ITreeNode } from 'rewild-ui';
import {
  containerFactory,
  baseActorTemplate,
} from '../utils/TemplateFactories';
import { Dispatcher } from 'rewild-common';
import { projectStore } from './ProjectStore';

export type SceneGraphEvents =
  | {
      kind: 'nodes-updated';
      nodes: ITreeNode<IResource>[];
    }
  | { kind: 'container-activated'; container: ITreeNode<IResource> }
  | {
      kind: 'node-removed';
      node: ITreeNode<IResource>;
    }
  | { kind: 'container-deactivated'; container: ITreeNode<IResource> }
  | { kind: 'resource-selected'; node: ITreeNode<IResource> | null };

export class SceneGraphStore {
  selectedContainerId: string | null = null;
  selectedResource: IResource | null = null;

  nodes: ITreeNode<IResource>[];
  readonly dispatcher = new Dispatcher<SceneGraphEvents>();

  constructor() {
    this.dispatcher.add(this.handleSceneGraphEvent);
    this.nodes = [];
  }

  handleSceneGraphEvent(event: SceneGraphEvents) {
    if (event.kind === 'nodes-updated') {
      projectStore.dirty = true;
      projectStore.dispatcher.dispatch({ kind: 'changed' });
    }
  }

  setActiveContainer(id: string | null) {
    const previousId = this.selectedContainerId;
    if (previousId === id) return;

    this.selectedContainerId = id;

    // Switching straight from one container to another must unload the previous
    // one first, or its assets stay in the scene alongside the new container's.
    if (previousId) {
      const previous = this.findNodeById(previousId);
      if (previous)
        this.dispatcher.dispatch({
          kind: 'container-deactivated',
          container: previous,
        });
    }

    if (id) {
      const node = this.findNodeById(id);
      if (node)
        this.dispatcher.dispatch({
          kind: 'container-activated',
          container: node,
        });
    }
  }

  buildTreeFromSceneGraph(sceneGraph: ISceneGraph) {
    this.nodes = [
      {
        name: 'Containers',
        factoryKey: 'container',
        canSelect: true,
        icon: 'boxes',
        id: 'CONTAINERS',
        template: containerFactory,
        children:
          sceneGraph.containers.map(
            (container) =>
              ({
                ...containerFactory(),
                name: container.name,
                resource: container,
                children: container.actors?.map(
                  (actor) =>
                    ({
                      ...baseActorTemplate,
                      icon: 'box',
                      name: actor.name,
                      resource: actor,
                    } as ITreeNode)
                ),
              } as ITreeNode)
          ) || [],
      } as ITemplateTreeNode,
      {
        name: 'Sky',
        factoryKey: 'sky',
        canRename: false,
        canSelect: true,
        icon: 'sun',
        id: 'SKY',
        resource: {
          id: 'SKY',
          name: 'Sky',
          type: 'actor',
          properties: [
            {
              type: 'cloudiness',
              value: sceneGraph.atmosphere?.cloudiness ?? 0.7,
            },
            {
              type: 'foginess',
              value: sceneGraph.atmosphere?.foginess ?? 0.3,
            },
            {
              type: 'windiness',
              value: sceneGraph.atmosphere?.windiness ?? 0.5,
            },
            {
              type: 'windDirection',
              value: sceneGraph.atmosphere?.windDirection ?? 180,
            },
            {
              type: 'temperature',
              value: sceneGraph.atmosphere?.temperature ?? 0.5,
            },
            {
              type: 'precipitation',
              value: sceneGraph.atmosphere?.precipitation ?? 0.0,
            },
            {
              type: 'elevation',
              value: sceneGraph.atmosphere?.elevation ?? 80,
            },
            {
              type: 'dayNightCycle',
              value: sceneGraph.atmosphere?.dayNightCycle ?? false,
            },
            {
              type: 'weatherState',
              value: sceneGraph.atmosphere?.weatherState ?? 'random',
            },
            {
              type: 'randomMoonPhase',
              value: sceneGraph.atmosphere?.randomMoonPhase ?? false,
            },
            {
              type: 'moonPhase',
              value: sceneGraph.atmosphere?.moonPhase ?? 0.5,
            },
          ],
        },
      } as ITreeNode<IResource>,
    ];

    const reParentNodes = (
      nodes: ITreeNode<IResource>[],
      parent: ITreeNode<IResource> | null
    ) => {
      for (const node of nodes) {
        node.parent = parent;
        if (node.children) {
          reParentNodes(node.children, node);
        }
      }
    };

    reParentNodes(this.nodes, null);

    this.dispatcher.dispatch({ kind: 'nodes-updated', nodes: this.nodes });
  }

  findNodeById(
    id: string,
    nodes: ITreeNode<IResource>[] | null = this.nodes
  ): ITreeNode<IResource> | null {
    if (!nodes) return null;

    for (const node of nodes) {
      if (node.resource?.id === id) {
        return node;
      }

      if (node.children) {
        const found = this.findNodeById(id, node.children);
        if (found) {
          return found;
        }
      }
    }

    return null;
  }

  buildObjectFromProperties(id: string) {
    const node = this.findNodeById(id);
    if (!node) {
      return null;
    }

    const obj = node.resource?.properties?.reduce((acc, cur) => {
      acc[cur.type] = cur.value;
      return acc;
    }, {} as { [key in PropertyType]: PropValue });

    return obj;
  }

  getNodePropertyValue(
    node: ITreeNode<IResource>,
    propertyType: PropertyType
  ): PropValue | null {
    const property = node.resource?.properties?.find(
      (prop) => prop.type === propertyType
    );
    return property ? property.value : null;
  }

  removeNode = (node: ITreeNode<IResource>) => {
    traverseTree(this.nodes, (n, parent) => {
      if (n === node) {
        parent!.children = parent!.children!.filter((child) => child !== node);
        return true;
      }

      return false;
    });
    this.dispatcher.dispatch({ kind: 'node-removed', node: node });
    this.dispatcher.dispatch({ kind: 'nodes-updated', nodes: this.nodes });

    node.parent = null;
  };

  setSelectedNode(node: ITreeNode<IResource> | null) {
    this.selectedResource = node ? node.resource || null : null;
    this.dispatcher.dispatch({ kind: 'resource-selected', node });
  }

  createChildNode(selectedNode: ITemplateTreeNode) {
    const newNode = selectedNode.template();
    newNode.parent = selectedNode;
    selectedNode.children = (selectedNode.children || []).concat(newNode);
    this.dispatcher.dispatch({ kind: 'nodes-updated', nodes: this.nodes });
  }

  addNode(
    node: ITreeNode<IResource>,
    parent?: ITreeNode<IResource> | null
  ): ITreeNode<IResource> {
    if (parent && !parent.children) parent.children = [];

    let nodes = parent?.children || this.nodes;
    node.parent = parent || null;

    nodes.push(node);
    this.dispatcher.dispatch({ kind: 'nodes-updated', nodes: this.nodes });
    return node;
  }
}

export const sceneGraphStore = new SceneGraphStore();
